import Vue from 'vue'
import { API_BASE_URL } from './config'

const STORAGE_TOKEN = 'admin_token'
const STORAGE_USER = 'admin_user'
const STORAGE_EXPIRY = 'admin_token_expiry'

// 解析后端返回的 expiresIn（字符串，如 "2h"）为毫秒数；无法解析时返回 0（不强制过期）
function parseExpiresIn(str) {
  if (!str) return 0
  const m = String(str).match(/^\s*(\d+)\s*(s|m|h|d)?\s*$/i)
  if (!m) return 0
  const n = parseInt(m[1], 10)
  const unit = (m[2] || 's').toLowerCase()
  const mul = { s: 1000, m: 60 * 1000, h: 60 * 60 * 1000, d: 24 * 60 * 60 * 1000 }[unit]
  return n * mul
}

// 轻量鉴权 store（无 vuex 环境下的简单实现）。
// 已对接真实后端：/api/admin/login、/api/admin/logout（见 API.md）。
const auth = new Vue({
  data: {
    token: localStorage.getItem(STORAGE_TOKEN) || '',
    user: JSON.parse(localStorage.getItem(STORAGE_USER) || 'null'),
    tokenExpiry: parseInt(localStorage.getItem(STORAGE_EXPIRY) || '0', 10) || 0,
  },
  computed: {
    isAuthenticated() {
      if (!this.token) return false
      // token 已过过期时间则视为未登录（后端 2h 过期，前端提前兜底）
      if (this.tokenExpiry && Date.now() >= this.tokenExpiry) return false
      return true
    },
  },
  methods: {
    // 登录：POST {BASE_URL}/api/admin/login
    // 成功：{ success:true, token, expiresIn, user:{username, role} }
    // 失败：HTTP 401/400，body { success:false, error } —— 抛出 error 文案给上层提示
    async login(username, password) {
      if (!username || !password) {
        throw new Error('请输入账号和密码')
      }

      const res = await fetch(`${API_BASE_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      })

      let data = {}
      try {
        data = await res.json()
      } catch (e) {
        data = {}
      }

      if (!res.ok || !data.success) {
        throw new Error((data && data.error) || `登录失败（HTTP ${res.status}）`)
      }

      const token = data.token
      const rawUser = data.user || {}
      const user = {
        username: rawUser.username || username,
        name: rawUser.username || username,
        role: rawUser.role || '',
      }
      const expiresMs = parseExpiresIn(data.expiresIn)

      this.token = token
      this.user = user
      this.tokenExpiry = expiresMs ? Date.now() + expiresMs : 0

      localStorage.setItem(STORAGE_TOKEN, token)
      localStorage.setItem(STORAGE_USER, JSON.stringify(user))
      if (this.tokenExpiry) {
        localStorage.setItem(STORAGE_EXPIRY, String(this.tokenExpiry))
      }

      return user
    },

    // 退出：POST {BASE_URL}/api/admin/logout，带 Bearer 头；无论后端成败都清本地登录态
    async logout() {
      const token = this.token
      if (token) {
        try {
          await fetch(`${API_BASE_URL}/api/admin/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          })
        } catch (e) {
          // 网络错误忽略，本地照常清理
        }
      }
      this._clear()
    },

    _clear() {
      this.token = ''
      this.user = null
      this.tokenExpiry = 0
      localStorage.removeItem(STORAGE_TOKEN)
      localStorage.removeItem(STORAGE_USER)
      localStorage.removeItem(STORAGE_EXPIRY)
    },

    // 组装带鉴权头的请求头（供后续受保护接口复用）
    authHeaders(extra = {}) {
      const h = { ...extra }
      if (this.token) h['Authorization'] = `Bearer ${this.token}`
      return h
    },

    // 后续受保护接口统一走这里：自动带 token；遇到 401 自动清登录态并跳登录页
    async request(url, options = {}) {
      const headers = this.authHeaders(options.headers || {})
      const res = await fetch(`${API_BASE_URL}${url}`, { ...options, headers })
      if (res.status === 401) {
        this._clear()
        if (typeof window !== 'undefined') {
          const to = window.location.pathname + window.location.search
          window.location.href = `/admin/login?redirect=${encodeURIComponent(to)}`
        }
        throw new Error('登录已失效，请重新登录')
      }
      return res
    },
  },
})

export default auth



// WEBPACK FOOTER //
// ./src/admin/auth.js